import {
  AnimalDto,
  AnimalAdoptionDtoStatus,
  CreateAnimalBodyDtoSex,
  AnimalDtoSpecies,
  CreateAnimalBodyDtoVaccinationStatus,
  AnimalHealthDto,
  AnimalHealthDtoFeLV,
  AnimalHealthDtoFIV,
  AnimalHealthOperationDtoType,
  AnimalSearchItemDto,
  AnimalTakeOverDtoReason,
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStep,
  AdministrativeFollowUpDtoType,
  AnimalTakeOverDtoOrigin,
  AnimalDocumentDtoType,
  AnimalFormAndActivityDtoSize,
  AnimalFormAndActivityDtoActivity,
  AnimalFormAndActivityDtoCorpulence,
  AnimalAdoptionDtoOfferType,
  AdministrativeFollowUpDtoStatus,
  AnimalBreedDtoBreedTitle,
  AnimalHealthDtoDogCategory,
  VaccinationOperationDtoType,
  AnimalVaccinationDtoVaccines,
  TattooDtoLocation,
  ContactHostedFamilyFollowUpDtoType,
  AnimalDtoHairLength,
  AnimalHealthDtoPathologies,
  AnimalDtoIdentificationNumberLocation,
  AnimalHealthDtoSterilizedForFerrets,
} from '../../interactors/gen/backendClient'
import { colors } from '../../views/theme'
import { HostedFamilyFollowUpDtoType } from './HostedFamilyFollowUpDtoType'

export function getAnimalIcon(animal: AnimalDto): string {
  if (animal.sex === 'male' && animal.species === 'cat') {
    return '🐈‍⬛⚦'
  } else if (animal.sex === 'female' && animal.species === 'cat') {
    return '🐈♀'
  } else if (animal.sex === 'male' && animal.species === 'dog') {
    return '🐕⚦'
  } else if (animal.sex === 'female' && animal.species === 'dog') {
    return '🐩♀'
  }

  return ''
}

export const getReadableAnimalAdoptionStatus = (field: AnimalAdoptionDtoStatus): string => {
  const map = {
    [AnimalAdoptionDtoStatus.Adoptable]: 'Adoptable',
    [AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable]: "En cours d'adoption",
    [AnimalAdoptionDtoStatus.HasBeenAdopted]: 'Adopté',
    [AnimalAdoptionDtoStatus.CannotBeAdopted]: 'Non adoptable',
    [AnimalAdoptionDtoStatus.ToBeFilled]: 'À compléter',
  }

  return map[field]
}

export const getColorForAnimalAdoptionStatus = (field: AnimalAdoptionDtoStatus): string => {
  const map = {
    [AnimalAdoptionDtoStatus.Adoptable]: colors.bermudaGray,
    [AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable]: colors.peach,
    [AnimalAdoptionDtoStatus.HasBeenAdopted]: colors.lightGreen,
    [AnimalAdoptionDtoStatus.CannotBeAdopted]: colors.geyser,
    [AnimalAdoptionDtoStatus.ToBeFilled]: '#EBCDD5',
  }

  return map[field]
}

export const getReadableAnimalAdoptionStep = (field: AnimalAdoptionDtoStep): string => {
  const map = {
    [AnimalAdoptionDtoStep.AdoptionToBeValidated]: 'Adoption à valider ',
    [AnimalAdoptionDtoStep.AdoptionValidatedWithoutPostVisit]: 'Adoption validée (sans VPA)',
    [AnimalAdoptionDtoStep.AdoptionValidatedAfterPostVisit]: 'Adoption validée après VPA',
    [AnimalAdoptionDtoStep.PostVisitNotValidated]: 'VPA non validée',
    [AnimalAdoptionDtoStep.AnimalReturned]: `Animal retourné à l'association`,
    [AnimalAdoptionDtoStep.AnimalReserved]: `Animal réservé`,
  }

  return map[field]
}

export const getColorForAnimalAdoptionStep = (field: AnimalAdoptionDtoStep): string => {
  const map = {
    [AnimalAdoptionDtoStep.AnimalReserved]: colors.mintTulip,
    [AnimalAdoptionDtoStep.AdoptionToBeValidated]: colors.amber,
    [AnimalAdoptionDtoStep.AdoptionValidatedWithoutPostVisit]: colors.cello,
    [AnimalAdoptionDtoStep.AdoptionValidatedAfterPostVisit]: colors.seaGreen,
    [AnimalAdoptionDtoStep.PostVisitNotValidated]: colors.punch,
    [AnimalAdoptionDtoStep.AnimalReturned]: colors.mintTulip,
  }

  return map[field]
}

export const getReadableCannotBeAdoptedStatus = (
  field: AnimalAdoptionDtoCannotBeAdoptedStatus,
  opts: { short: boolean } = { short: false }
): string => {
  if (opts.short) {
    const map: Record<AnimalAdoptionDtoCannotBeAdoptedStatus, string> = {
      [AnimalAdoptionDtoCannotBeAdoptedStatus.Released]: 'Relaché',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.Dead]: 'Décès',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.InCare]: 'En soin',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily]: `FALD`,
      [AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized]: `En sociabilisation`,
      [AnimalAdoptionDtoCannotBeAdoptedStatus.Lost]: 'Perdu',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation]: 'Cédé à une autre asso',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation]: 'En gestation',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation]: 'En lactation',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.InReeducation]: 'En rééducation',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.Sanctuarized]: 'Sanctuarisé',
      [AnimalAdoptionDtoCannotBeAdoptedStatus.SocialSecurity]: 'Garde sociale',
    }

    return map[field]
  }

  const map: Record<AnimalAdoptionDtoCannotBeAdoptedStatus, string> = {
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Released]: 'Relaché (Animal Libre)',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Dead]: 'Décès',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InCare]: 'En soin',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily]: `Famille d'accueil longue durée`,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized]: `En sociabilisation`,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Lost]: 'Perdu',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation]: 'Cédé à une autre association',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation]: 'En gestation',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation]: 'En lactation',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InReeducation]: 'En rééducation',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Sanctuarized]: 'Sanctuarisé',
    [AnimalAdoptionDtoCannotBeAdoptedStatus.SocialSecurity]: 'Garde sociale',
  }

  return map[field]
}

export const getColorForCannotBeAdoptedStatus = (field: AnimalAdoptionDtoCannotBeAdoptedStatus): string => {
  const map: Record<AnimalAdoptionDtoCannotBeAdoptedStatus, string> = {
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Released]: colors.cello,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Dead]: colors.black,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InCare]: colors.azalea,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily]: colors.mintTulip,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.BeingSocialized]: colors.anakiwa,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Lost]: colors.silver,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation]: colors.amber,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InGestation]: colors.azalea,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InLactation]: colors.azalea,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.InReeducation]: colors.azalea,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.Sanctuarized]: colors.anakiwa,
    [AnimalAdoptionDtoCannotBeAdoptedStatus.SocialSecurity]: colors.azalea,
  }

  return map[field]
}

export const getReadableAnimalVaccineStatus = (field: CreateAnimalBodyDtoVaccinationStatus): string => {
  const map: Record<CreateAnimalBodyDtoVaccinationStatus, string> = {
    [CreateAnimalBodyDtoVaccinationStatus.ToDo]: 'À initier',
    [CreateAnimalBodyDtoVaccinationStatus.InProgress]: 'En cours',
    [CreateAnimalBodyDtoVaccinationStatus.Completed]: 'Complet',
    [CreateAnimalBodyDtoVaccinationStatus.Unknown]: 'Ne sais pas',
    [CreateAnimalBodyDtoVaccinationStatus.NotDoing]: "N'est pas concerné",
  }

  return map[field]
}

export const getColorForAnimalVaccineStatus = (field: CreateAnimalBodyDtoVaccinationStatus): string => {
  const map: Record<CreateAnimalBodyDtoVaccinationStatus, string> = {
    [CreateAnimalBodyDtoVaccinationStatus.ToDo]: colors.mintTulip,
    [CreateAnimalBodyDtoVaccinationStatus.InProgress]: colors.amber,
    [CreateAnimalBodyDtoVaccinationStatus.Completed]: colors.seaGreen,
    [CreateAnimalBodyDtoVaccinationStatus.Unknown]: colors.athensGray,
    [CreateAnimalBodyDtoVaccinationStatus.NotDoing]: colors.azalea,
  }

  return map[field]
}

export const getReadableOperationType = (field: AnimalHealthOperationDtoType): string => {
  const map: Record<AnimalHealthOperationDtoType, string> = {
    [AnimalHealthOperationDtoType.Deworming]: 'Vermifuge',
    [AnimalHealthOperationDtoType.Others]: 'Autres visites vétérinaires',
    [AnimalHealthOperationDtoType.Identification]: 'Identification',
    [AnimalHealthOperationDtoType.SterilizedOrNeutered]: 'Stérilisation / Castration',
    [AnimalHealthOperationDtoType.FleaControl]: 'Antipuce',
    [AnimalHealthOperationDtoType.Tatoo]: 'Tatouage',
    [AnimalHealthOperationDtoType.Implant]: 'Implant',
    [AnimalHealthOperationDtoType.EarMarking]: "Marquage à l'oreille",
    [AnimalHealthOperationDtoType.GoodHealthCertificate]: 'Certificat de bonne santé',
    [AnimalHealthOperationDtoType.AnnualReminder]: 'Rappel annuel',
    [AnimalHealthOperationDtoType.FirstInjection]: 'Première injection',
    [AnimalHealthOperationDtoType.SecondInjection]: 'Deuxième injection',
    [AnimalHealthOperationDtoType.ThirdInjection]: 'Troisième injection',
    [AnimalHealthOperationDtoType.OtherVaccines]: 'Autres vaccins',
    [AnimalHealthOperationDtoType.Surgery]: 'Chirurgie',
    [AnimalHealthOperationDtoType.IdentificationChip]: "Puce d'identification",
    [AnimalHealthDtoPathologies.CanineDistemper]: 'Test Maladie de carré',
    [AnimalHealthDtoPathologies.RubarthHepatitis]: 'Test Hépatite de Rubarth',
    [AnimalHealthDtoPathologies.Leptospirosis]: 'Test Leptospirose',
    [AnimalHealthDtoPathologies.Rabies]: 'Test Rage',
    [AnimalHealthDtoPathologies.Parvovirus]: 'Test Parvovirose',
    [AnimalHealthDtoPathologies.KennelCough]: 'Test Toux de chenil',
    [AnimalHealthDtoPathologies.Herpesvirus]: 'Test Herpèsvirose',
    [AnimalHealthDtoPathologies.Babesiosis]: 'Test Piroplasmose',
    [AnimalHealthDtoPathologies.LymeDisease]: 'Test Maladie de Lyme',
    [AnimalHealthDtoPathologies.Diabetic]: 'Test Diabète',
    [AnimalHealthDtoPathologies.Typhus]: 'Test Typhus',
    [AnimalHealthDtoPathologies.Chlamydiosis]: 'Test Chlamydiose',
    [AnimalHealthDtoPathologies.Coryza]: 'Test Coryza',
    [AnimalHealthOperationDtoType.Fiv]: 'Test FIV',
    [AnimalHealthOperationDtoType.FeLv]: 'Test FeLV',
    [AnimalHealthDtoPathologies.Fip]: 'Test PIF',
    [AnimalHealthDtoPathologies.CatScratchDisease]: 'Test Maladie des griffes du chat',
    [AnimalHealthOperationDtoType.Hospitalization]: 'Hospitalisation',
    [AnimalHealthOperationDtoType.BloodTest]: 'Prise de sang',
    [AnimalHealthOperationDtoType.Treatment]: 'Traitement',
    [AnimalHealthOperationDtoType.MedicalAppointment]: 'Consultation',
    [AnimalHealthOperationDtoType.Giardiasis]: 'Test Giardiose',
    [AnimalHealthOperationDtoType.Coronavirus]: 'Test Coronavirus',
    [AnimalHealthOperationDtoType.Dentist]: 'Dentiste',
    [AnimalHealthOperationDtoType.Podology]: 'Podologue',
    [AnimalHealthOperationDtoType.Ostheopathy]: 'Ostéopathe',
    [AnimalHealthOperationDtoType.OtherMedicalCare]: 'Autres médecines',
    [AnimalHealthOperationDtoType.Euthanasia]: 'Euthanasie',
    [AnimalHealthOperationDtoType.XRay]: 'Radiographie',
    [AnimalHealthOperationDtoType.Ultrasound]: 'Échographie',
  }

  return map[field]
}

export const getColorForOperationType = (field: AnimalHealthOperationDtoType): string => {
  const map: Record<AnimalHealthOperationDtoType, string> = {
    [AnimalHealthOperationDtoType.Deworming]: colors.mintTulip,
    [AnimalHealthOperationDtoType.Others]: colors.geyser,
    [AnimalHealthOperationDtoType.Identification]: colors.lightGreen,
    [AnimalHealthOperationDtoType.SterilizedOrNeutered]: colors.azalea,
    [AnimalHealthOperationDtoType.FleaControl]: colors.mintTulip,
    [AnimalHealthOperationDtoType.Tatoo]: colors.lightGreen,
    [AnimalHealthOperationDtoType.Implant]: colors.azalea,
    [AnimalHealthOperationDtoType.EarMarking]: colors.lightGreen,
    [AnimalHealthOperationDtoType.GoodHealthCertificate]: colors.peach,
    [AnimalHealthOperationDtoType.AnnualReminder]: colors.mintTulip,
    [AnimalHealthOperationDtoType.FirstInjection]: colors.mintTulip,
    [AnimalHealthOperationDtoType.SecondInjection]: colors.mintTulip,
    [AnimalHealthOperationDtoType.ThirdInjection]: colors.mintTulip,
    [AnimalHealthOperationDtoType.OtherVaccines]: colors.mintTulip,
    [AnimalHealthOperationDtoType.Surgery]: colors.azalea,
    [AnimalHealthOperationDtoType.IdentificationChip]: colors.lightGreen,
    [AnimalHealthDtoPathologies.CanineDistemper]: colors.peach,
    [AnimalHealthDtoPathologies.RubarthHepatitis]: colors.peach,
    [AnimalHealthDtoPathologies.Leptospirosis]: colors.peach,
    [AnimalHealthDtoPathologies.Rabies]: colors.peach,
    [AnimalHealthDtoPathologies.Parvovirus]: colors.peach,
    [AnimalHealthDtoPathologies.KennelCough]: colors.peach,
    [AnimalHealthDtoPathologies.Herpesvirus]: colors.peach,
    [AnimalHealthDtoPathologies.Babesiosis]: colors.peach,
    [AnimalHealthDtoPathologies.LymeDisease]: colors.peach,
    [AnimalHealthDtoPathologies.Diabetic]: colors.peach,
    [AnimalHealthDtoPathologies.Typhus]: colors.peach,
    [AnimalHealthDtoPathologies.Chlamydiosis]: colors.peach,
    [AnimalHealthDtoPathologies.Coryza]: colors.peach,
    [AnimalHealthOperationDtoType.Fiv]: colors.peach,
    [AnimalHealthOperationDtoType.FeLv]: colors.peach,
    [AnimalHealthDtoPathologies.Fip]: colors.peach,
    [AnimalHealthDtoPathologies.CatScratchDisease]: colors.peach,
    [AnimalHealthOperationDtoType.Hospitalization]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.BloodTest]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.Treatment]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.MedicalAppointment]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.Giardiasis]: colors.peach,
    [AnimalHealthOperationDtoType.Coronavirus]: colors.peach,
    [AnimalHealthOperationDtoType.Dentist]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.Podology]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.Ostheopathy]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.OtherMedicalCare]: colors.bermudaGray,
    [AnimalHealthOperationDtoType.Euthanasia]: colors.red,
    [AnimalHealthOperationDtoType.XRay]: colors.azalea,
    [AnimalHealthOperationDtoType.Ultrasound]: colors.azalea,
  }

  return map[field]
}

export const getReadableSpecies = (
  species: AnimalDtoSpecies,
  { withEmoji }: { withEmoji: boolean } = { withEmoji: false }
): string => {
  const map = {
    [AnimalDtoSpecies.Cat]: 'Chat',
    [AnimalDtoSpecies.Dog]: 'Chien',
    [AnimalDtoSpecies.Birds]: 'Oiseau',
    [AnimalDtoSpecies.GuineaPig]: 'Cochon d’Inde',
    [AnimalDtoSpecies.Hamster]: 'Hamster',
    [AnimalDtoSpecies.Mouse]: 'Souris',
    [AnimalDtoSpecies.Rat]: 'Rat',
    [AnimalDtoSpecies.Rabbit]: 'Lapin',
    [AnimalDtoSpecies.Ferret]: 'Furet',
    [AnimalDtoSpecies.Equidae]: 'Equidé',
    [AnimalDtoSpecies.Pogonas]: 'Pogana',
    [AnimalDtoSpecies.Lizard]: 'Lézard',
    [AnimalDtoSpecies.Snake]: 'Serpent',
    [AnimalDtoSpecies.Turtle]: 'Tortue',
    [AnimalDtoSpecies.Pigs]: 'Porcin',
    [AnimalDtoSpecies.Cattle]: 'Bovin',
    [AnimalDtoSpecies.Sheep]: 'Ovin',
    [AnimalDtoSpecies.Goats]: 'Caprin',
    [AnimalDtoSpecies.Poultry]: 'Volaille',
    [AnimalDtoSpecies.Chinchilla]: 'Chinchilla',
  }

  const readableSpecies = map[species]
  if (withEmoji) {
    return `${getEmojiSpecies(species)} ${readableSpecies}`
  }

  return readableSpecies
}

export const getEmojiSpecies = (field: AnimalDtoSpecies): string => {
  const map = {
    [AnimalDtoSpecies.Cat]: '🐱',
    [AnimalDtoSpecies.Dog]: '🐶',
    [AnimalDtoSpecies.Birds]: '🦉',
    [AnimalDtoSpecies.GuineaPig]: '🐹',
    [AnimalDtoSpecies.Hamster]: '🐹',
    [AnimalDtoSpecies.Mouse]: '🐹',
    [AnimalDtoSpecies.Rat]: '🐹',
    [AnimalDtoSpecies.Chinchilla]: '🐹',
    [AnimalDtoSpecies.Rabbit]: '🐰',
    [AnimalDtoSpecies.Ferret]: '🐹',
    [AnimalDtoSpecies.Equidae]: '🐴',
    [AnimalDtoSpecies.Pogonas]: '🦎',
    [AnimalDtoSpecies.Lizard]: '🦎',
    [AnimalDtoSpecies.Snake]: '🐍',
    [AnimalDtoSpecies.Turtle]: '🐢',
    [AnimalDtoSpecies.Pigs]: '🐷',
    [AnimalDtoSpecies.Cattle]: '🐮',
    [AnimalDtoSpecies.Sheep]: '🐑',
    [AnimalDtoSpecies.Goats]: '🐐',
    [AnimalDtoSpecies.Poultry]: '🐔',
  }

  return map[field]
}

export const getReadableSex = (
  sex: CreateAnimalBodyDtoSex,
  { withEmoji }: { withEmoji: boolean } = { withEmoji: false }
): string => {
  const map = {
    [CreateAnimalBodyDtoSex.Female]: 'Femelle',
    [CreateAnimalBodyDtoSex.Male]: 'Mâle',
  }

  const readableSpecies = map[sex]
  if (withEmoji) {
    return `${getEmojiAnimalSex(sex)} ${readableSpecies}`
  }

  return readableSpecies || sex
}

export const getEmojiAnimalSex = (field: CreateAnimalBodyDtoSex): string => {
  const map = {
    [CreateAnimalBodyDtoSex.Female]: '♀',
    [CreateAnimalBodyDtoSex.Male]: '⚦',
  }

  return map[field]
}

type AnimalPropertyWithLabel = Exclude<
  keyof AnimalDto | keyof AnimalSearchItemDto,
  | 'customerId'
  | 'birthday'
  | 'havePassport'
  | 'vaccination'
  | 'responsibilities'
  | 'adoption'
  | 'health'
  | 'images'
  | 'hostedFamilyFollowUps'
  | 'adoptionStatus'
  | 'takeOver'
  | 'sterilizedOrNeutered'
  | 'cannotBeAdoptedStatus'
  | 'insights'
  | 'insightsCounters'
  | 'documents'
  | 'announce'
>

export const getReadableAnimalFieldLabel = (field: AnimalPropertyWithLabel): string => {
  const map: Record<AnimalPropertyWithLabel, string> = {
    identificationNumber: 'N° de puce',
    takeOverAt: 'Date de prise en charge',
    id: 'ID',
    name: 'Nom',
    sex: 'Sexe',
    lifeBeforeAssociation: 'Notes et penses-bêtes',
    description: 'Description',
    species: 'Espèce',
    breed: 'Type de race',
    color: 'Robe et couleur',
    weightInKgs: 'Poids (en Kg)',
    hair: 'Type de poils',
    adoptionStep: "Étape d'adoption",
    formAndActivity: 'Forme et activité',
    breedData: 'Portée et généalogie',
    characteristics: 'Caractéristiques',
    identifyingMarks: 'Signes distinctifs',
    tattoo: 'Tatouage',
    birthdate: 'Date de naissance',
    hairLength: 'Poils',
    identificationNumberLocation: 'Localisation du numéro d’identification',
    newName: 'Nouveau nom',
    gpsPoint: 'Coordonnées GPS',
    shareAnnounce: 'Partage de l’annonce',
    deceasedAfterAdoption: 'Décès après adoption',
    sanitarySurveillanceUntil: 'En surveillance sanitaire',
  }

  return map[field]
}

type AnimalHealthPropertyWithLabel = Exclude<
  keyof AnimalHealthDto,
  'sterilizedOrNeutered' | 'FIV' | 'FeLV' | 'operations' | 'sterilizedForFerrets'
> // Exclude sterilizedOrNeutered as it depends on the sex

export const getReadableAnimalHealthFieldLabel = (field: AnimalHealthPropertyWithLabel): string => {
  const map: Record<AnimalHealthPropertyWithLabel, string> = {
    healthHistory: 'Historique de Santé',
    hasEuropeanPassport: "Détention d'un passeport Européen",
    dogCategory: 'Chien de catégorie',
    pathologies: 'Pathologies',
    alimentation: 'Alimentation',
    dewormingFrequencyInMonths: 'Fréquence de vermifugation (en mois)',
    fleaControlFrequencyInMonths: 'Fréquence de traitement antipuce (en mois)',
    sanitarySurveillanceUntil: 'En surveillance sanitaire',
  }

  return map[field]
}

enum TestStatus {
  Positive = 'positive',
  Negative = 'negative',
  Unknown = 'unknown',
}

export const getReadableAnimalHealthTestStatus = (field: AnimalHealthDtoFeLV | AnimalHealthDtoFIV): string => {
  const map: Record<TestStatus, string> = {
    [TestStatus.Positive]: 'Positif',
    [TestStatus.Negative]: 'Negatif',
    [TestStatus.Unknown]: 'Ne sais pas',
  }

  return map[field]
}

export const getColorForAnimalHealthTestStatus = (field: AnimalHealthDtoFeLV | AnimalHealthDtoFIV): string => {
  const map: Record<TestStatus, string> = {
    [TestStatus.Positive]: colors.punch,
    [TestStatus.Negative]: colors.seaGreen,
    [TestStatus.Unknown]: colors.amber,
  }

  return map[field]
}

export const getReadableTakeOverOrigin = (field: AnimalTakeOverDtoOrigin): string => {
  const map: Record<AnimalTakeOverDtoOrigin, string> = {
    [AnimalTakeOverDtoOrigin.BirthInCaptivity]: 'Naissance en captivité',
    [AnimalTakeOverDtoOrigin.CaptureInTheWild]: 'Capture dans le milieu naturel',
    [AnimalTakeOverDtoOrigin.Unknown]: 'Inconnue',
  }

  return map[field]
}

export const getReadableTakeOverReason = (field: AnimalTakeOverDtoReason): string => {
  const map: Record<AnimalTakeOverDtoReason, string> = {
    [AnimalTakeOverDtoReason.AbandonmentVeterinaryClinic]: 'Cession - Clinique vétérinaire',
    [AnimalTakeOverDtoReason.AbandonmentPound]: 'Cession - Fourrière',
    [AnimalTakeOverDtoReason.DeathOfTheOwner]: 'Cession particulier (e.g. Décès)',
    [AnimalTakeOverDtoReason.Others]: 'Autres abandon',
    [AnimalTakeOverDtoReason.AbandonmentTownHall]: 'Cession - Mairie',
    [AnimalTakeOverDtoReason.AbandonmentAssociation]: 'Cession - Association',
    [AnimalTakeOverDtoReason.AbandonmentIndividual]: 'Abandon - Particulier',
    [AnimalTakeOverDtoReason.AnimalSeizure]: 'Saisie',
    [AnimalTakeOverDtoReason.Trapping]: 'Trappage',
    [AnimalTakeOverDtoReason.Birth]: 'Naissance',
  }

  return map[field]
}

export const normalizeIdentificationNumber = (identifier: string | number): string => {
  const asString = String(identifier).trim()

  if (!/[eE]/.test(asString)) {
    return asString
  }

  const asNumber = Number(asString)

  if (!Number.isFinite(asNumber)) {
    return asString
  }

  return asNumber.toLocaleString('fullwide', { useGrouping: false })
}

export const getReadableIdentificationNumber = (identifier: string): string => {
  const normalizedIdentifier = normalizeIdentificationNumber(identifier)
  if (normalizedIdentifier.length === 15) {
    return splitOn(normalizedIdentifier, 3, 5, 7).join(' ')
  }

  if (normalizedIdentifier.length === 7) {
    return splitOn(normalizedIdentifier, 1, 4).join(' ')
  }

  return splitOn(normalizedIdentifier, 3).join(' ')
}

const splitOn: {
  <T = string>(s: T, ...i: number[]): T[]
  <T extends any[]>(s: T, ...i: number[]): T[]
} = <T>(slicable: string | T[], ...indices: number[]) => [0, ...indices].map((n, i, m) => slicable.slice(n, m[i + 1]))

export const getReadableHostedFamilyFollowUpType = (field: HostedFamilyFollowUpDtoType): string => {
  const map: Record<HostedFamilyFollowUpDtoType, string> = {
    [HostedFamilyFollowUpDtoType.InitialPlacement]: 'Placement',
    [HostedFamilyFollowUpDtoType.Shift]: 'Déplacement',
    [HostedFamilyFollowUpDtoType.ChangeOfHostedFamily]: "Changement de famille d'accueil",
    [HostedFamilyFollowUpDtoType.Exit]: 'Sortie',
  }

  return map[field]
}

export const getColorForHostedFamilyFollowUpType = (field: HostedFamilyFollowUpDtoType): string => {
  const map: Record<HostedFamilyFollowUpDtoType, string> = {
    [HostedFamilyFollowUpDtoType.InitialPlacement]: colors.mintTulip,
    [HostedFamilyFollowUpDtoType.Shift]: colors.peach,
    [HostedFamilyFollowUpDtoType.ChangeOfHostedFamily]: colors.bermudaGray,
    [HostedFamilyFollowUpDtoType.Exit]: colors.silver,
  }

  return map[field]
}

export const getReadableContactFollowUpType = (field: ContactHostedFamilyFollowUpDtoType): string => {
  const map: Record<ContactHostedFamilyFollowUpDtoType, string> = {
    [ContactHostedFamilyFollowUpDtoType.Physical]: 'Physique',
    [ContactHostedFamilyFollowUpDtoType.Phone]: 'Au téléphone/visio conférence',
  }
  return map[field]
}

export const getColorForContactFollowUpType = (field: ContactHostedFamilyFollowUpDtoType): string => {
  const map: Record<ContactHostedFamilyFollowUpDtoType, string> = {
    [ContactHostedFamilyFollowUpDtoType.Physical]: colors.mintTulip,
    [ContactHostedFamilyFollowUpDtoType.Phone]: colors.anakiwa,
  }
  return map[field]
}

export const getReadableAdministrativeFollowUpDtoType = (field: AdministrativeFollowUpDtoType): string => {
  const map: Record<AdministrativeFollowUpDtoType, string> = {
    [AdministrativeFollowUpDtoType.MakingContact]: 'Prise de contact',
    [AdministrativeFollowUpDtoType.HomeVisit]: 'Visite du domicile',
    [AdministrativeFollowUpDtoType.AnimalMeeting]: "Rencontre avec l'animal",
    [AdministrativeFollowUpDtoType.IssueOfCertificate]: 'Émission certificat',
    [AdministrativeFollowUpDtoType.ReceiptOfSignedCertificate]: 'Réception certificat signé',
    [AdministrativeFollowUpDtoType.IssueOfAdoptionContract]: `Émission du contrat d'adoption`,
    [AdministrativeFollowUpDtoType.ReceiptOfSignedContract]: 'Réception du contrat signé',
  }

  return map[field]
}

export const getColorForAdministrativeFollowUpDtoType = (field: AdministrativeFollowUpDtoType): string => {
  const map: Record<AdministrativeFollowUpDtoType, string> = {
    [AdministrativeFollowUpDtoType.MakingContact]: colors.mintTulip,
    [AdministrativeFollowUpDtoType.HomeVisit]: colors.anakiwa,
    [AdministrativeFollowUpDtoType.AnimalMeeting]: colors.anakiwa,
    [AdministrativeFollowUpDtoType.IssueOfCertificate]: colors.amber,
    [AdministrativeFollowUpDtoType.ReceiptOfSignedCertificate]: colors.cello,
    [AdministrativeFollowUpDtoType.IssueOfAdoptionContract]: colors.amber,
    [AdministrativeFollowUpDtoType.ReceiptOfSignedContract]: colors.seaGreen,
  }

  return map[field]
}

export const getReadableAnimalDocumentType = (field: AnimalDocumentDtoType): string => {
  const map: Record<AnimalDocumentDtoType, string> = {
    [AnimalDocumentDtoType.IdentificationCertificate]: "Certificat d'identification",
    [AnimalDocumentDtoType.VeterinaryCertificate]: 'Certificat Vétérinaire',
    [AnimalDocumentDtoType.AdoptionContract]: "Contrat d'adoption",
    [AnimalDocumentDtoType.CopyOfDeclarationOfBirth]: 'Copie de la déclaration de Naissance',
    [AnimalDocumentDtoType.TransferCertificate]: 'Certificat de cession',
    [AnimalDocumentDtoType.TransferInvoice]: 'Attestation de cession ou certificat de vente',
    [AnimalDocumentDtoType.HealthBooklet]: 'Carnet de santé',
    [AnimalDocumentDtoType.CertificateOfGoodHealth]: 'Certificat de bonne santé',
    [AnimalDocumentDtoType.PrimovaccinationCertificate]: 'Certificat de primovaccination',
    [AnimalDocumentDtoType.TestResult]: 'Résultat de test',
    [AnimalDocumentDtoType.InformationDocument]: "Document d'information",
    [AnimalDocumentDtoType.VeterinaryOrderForm]: 'Bon vétérinaire',
    [AnimalDocumentDtoType.SignedAdoptionCertificate]: "Certificat d'adoption signé",
    [AnimalDocumentDtoType.SignedAdoptionContract]: "Contrat d'adoption signé",
    [AnimalDocumentDtoType.Others]: 'Autres documents',
    [AnimalDocumentDtoType.SterilizationCertificate]: 'Bon de stérilisation',
    [AnimalDocumentDtoType.NeuteringCertificate]: 'Bon de castration',
    [AnimalDocumentDtoType.VaccinationBooklet]: 'Carnet de vaccination',
    [AnimalDocumentDtoType.EuropeanPassport]: 'Passport Européen',
    [AnimalDocumentDtoType.WelcomeContract]: 'Contrat d’accueil',
  }

  return map[field]
}

export const getColorForAnimalDocumentType = (field: AnimalDocumentDtoType): string => {
  const map: Record<AnimalDocumentDtoType, string> = {
    [AnimalDocumentDtoType.IdentificationCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.VeterinaryCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.AdoptionContract]: colors.bermudaGray,
    [AnimalDocumentDtoType.CopyOfDeclarationOfBirth]: colors.bermudaGray,
    [AnimalDocumentDtoType.TransferCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.TransferInvoice]: colors.bermudaGray,
    [AnimalDocumentDtoType.HealthBooklet]: colors.bermudaGray,
    [AnimalDocumentDtoType.CertificateOfGoodHealth]: colors.bermudaGray,
    [AnimalDocumentDtoType.PrimovaccinationCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.TestResult]: colors.bermudaGray,
    [AnimalDocumentDtoType.InformationDocument]: colors.bermudaGray,
    [AnimalDocumentDtoType.VeterinaryOrderForm]: colors.amber,
    [AnimalDocumentDtoType.SignedAdoptionCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.SignedAdoptionContract]: colors.bermudaGray,
    [AnimalDocumentDtoType.SterilizationCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.NeuteringCertificate]: colors.bermudaGray,
    [AnimalDocumentDtoType.VaccinationBooklet]: colors.bermudaGray,
    [AnimalDocumentDtoType.EuropeanPassport]: colors.bermudaGray,
    [AnimalDocumentDtoType.WelcomeContract]: colors.bermudaGray,
    [AnimalDocumentDtoType.Others]: colors.mintTulip,
  }

  return map[field]
}

export const getReadableAnimalSize = (field: AnimalFormAndActivityDtoSize): string => {
  const map: Record<AnimalFormAndActivityDtoSize, string> = {
    [AnimalFormAndActivityDtoSize.VerySmall]: 'Très petite taille',
    [AnimalFormAndActivityDtoSize.Small]: 'Petite taille',
    [AnimalFormAndActivityDtoSize.Medium]: 'Taille moyenne',
    [AnimalFormAndActivityDtoSize.Large]: 'Grande taille',
    [AnimalFormAndActivityDtoSize.VeryLarge]: 'Très grande taille',
  }

  return map[field]
}

export const getReadableAnimalCorpulence = (field: AnimalFormAndActivityDtoCorpulence): string => {
  const map: Record<AnimalFormAndActivityDtoCorpulence, string> = {
    [AnimalFormAndActivityDtoCorpulence.Underweight]: 'Sous-poids',
    [AnimalFormAndActivityDtoCorpulence.Balanced]: 'Equilibré(e)',
    [AnimalFormAndActivityDtoCorpulence.Overweight]: 'Sur-poids',
    [AnimalFormAndActivityDtoCorpulence.ABitOverweight]: 'Un peu en sur-poids',
    [AnimalFormAndActivityDtoCorpulence.ABitUnderweight]: 'Un peu en sous-poids',
  }

  return map[field]
}

export const getReadableAnimalActivity = (field: AnimalFormAndActivityDtoActivity): string => {
  const map: Record<AnimalFormAndActivityDtoActivity, string> = {
    [AnimalFormAndActivityDtoActivity.Low]: 'Peu actif',
    [AnimalFormAndActivityDtoActivity.Medium]: 'Actif',
    [AnimalFormAndActivityDtoActivity.High]: 'Très actif',
    [AnimalFormAndActivityDtoActivity.VeryLow]: 'Très peu actif',
  }

  return map[field]
}

export const getReadableAnimalAdoptionOfferType = (field: AnimalAdoptionDtoOfferType): string => {
  const map: Record<AnimalAdoptionDtoOfferType, string> = {
    [AnimalAdoptionDtoOfferType.Donation]: 'Don',
    [AnimalAdoptionDtoOfferType.FreeAmount]: 'Montant libre',
    [AnimalAdoptionDtoOfferType.HealthCostCompensation]: "Frais d'adoption fixe",
    [AnimalAdoptionDtoOfferType.SaleOffer]: 'Vente',
  }

  return map[field]
}

export const animalAdoptionOfferTypeOrder: AnimalAdoptionDtoOfferType[] = [
  AnimalAdoptionDtoOfferType.HealthCostCompensation,
  AnimalAdoptionDtoOfferType.FreeAmount,
  AnimalAdoptionDtoOfferType.Donation,
  AnimalAdoptionDtoOfferType.SaleOffer,
]

export const isFreeAmountAdoptionOffer = (field?: AnimalAdoptionDtoOfferType | null): boolean => {
  return field === AnimalAdoptionDtoOfferType.FreeAmount
}

export const getAdoptionAmountDisplay = (
  offerType?: AnimalAdoptionDtoOfferType | null,
  priceInEuros?: number | null,
  paymentAmountInEuros?: number | null
): string => {
  if (offerType === AnimalAdoptionDtoOfferType.Donation) {
    return 'Don'
  }

  if (offerType === AnimalAdoptionDtoOfferType.FreeAmount) {
    if (paymentAmountInEuros != null) {
      return `${paymentAmountInEuros}€ payé`
    }

    return priceInEuros != null ? `Montant libre (min. ${priceInEuros}€)` : 'Montant libre'
  }

  return priceInEuros != null ? `${priceInEuros}€` : '0€'
}

export const getReadableAdministrativeFollowUpStatus = (field: AdministrativeFollowUpDtoStatus): string => {
  const map: Record<AdministrativeFollowUpDtoStatus, string> = {
    [AdministrativeFollowUpDtoStatus.Done]: 'Fait',
    [AdministrativeFollowUpDtoStatus.OnGoing]: 'En cours',
    [AdministrativeFollowUpDtoStatus.ToPlan]: 'A planifier',
  }

  return map[field]
}

export const getReadableAnimalBreedTitle = (field: AnimalBreedDtoBreedTitle): string => {
  const map: Record<AnimalBreedDtoBreedTitle, string> = {
    [AnimalBreedDtoBreedTitle.BreedType]: 'Type de race',
    [AnimalBreedDtoBreedTitle.Purebred]: 'Race',
    [AnimalBreedDtoBreedTitle.MixedBreed]: 'Croisé',
  }

  return map[field]
}

export const getReadableAnimalDogCategory = (field: AnimalHealthDtoDogCategory): string => {
  const map: Record<AnimalHealthDtoDogCategory, string> = {
    [AnimalHealthDtoDogCategory.One]: '1',
    [AnimalHealthDtoDogCategory.Two]: '2',
    [AnimalHealthDtoDogCategory.Three]: '3',
    [AnimalHealthDtoDogCategory.Other]: 'Aucune des trois',
  }

  return map[field]
}

export const getReadableVaccinationOperationType = (field: VaccinationOperationDtoType): string => {
  const map: Record<VaccinationOperationDtoType, string> = {
    [VaccinationOperationDtoType.AnnualReminder]: 'Rappel annuel',
    [VaccinationOperationDtoType.FirstInjection]: 'Première injection',
    [VaccinationOperationDtoType.SecondInjection]: 'Deuxième injection',
    [VaccinationOperationDtoType.ThirdInjection]: 'Troisième injection',
  }

  return map[field]
}

export const getReadableVaccines = (field: AnimalVaccinationDtoVaccines): string => {
  const map: Record<AnimalVaccinationDtoVaccines, string> = {
    [AnimalVaccinationDtoVaccines.Chppi]: 'CHPPi',
    [AnimalVaccinationDtoVaccines.Clamoxydose]: 'Clamoxydose',
    [AnimalVaccinationDtoVaccines.Herpesvirose]: 'Herpesvirose',
    [AnimalVaccinationDtoVaccines.Leptospirose]: 'Leptospirose (L)',
    [AnimalVaccinationDtoVaccines.Leucose]: 'Leucose',
    [AnimalVaccinationDtoVaccines.Rage]: 'Rage',
    [AnimalVaccinationDtoVaccines.Tc]: 'TC',
    [AnimalVaccinationDtoVaccines.KennelCough]: 'Toux de chenil',
  }
  return map[field]
}

export const getReadableTattooLocation = (field: TattooDtoLocation): string => {
  const map: Record<TattooDtoLocation, string> = {
    [TattooDtoLocation.LeftEar]: 'Oreille gauche',
    [TattooDtoLocation.LeftGroin]: 'Aine gauche',
    [TattooDtoLocation.RightEar]: 'Oreille droite',
    [TattooDtoLocation.RightGroin]: 'Aine droite',
    [TattooDtoLocation.Other]: 'Autre',
  }

  return map[field]
}

export const getReadableIdentificationNumberLocation = (field: AnimalDtoIdentificationNumberLocation): string => {
  const map: Record<AnimalDtoIdentificationNumberLocation, string> = {
    [AnimalDtoIdentificationNumberLocation.LeftJugularGroove]: 'Gouttière jugulaire gauche',
    [AnimalDtoIdentificationNumberLocation.RightJugularGroove]: 'Gouttière jugulaire droite',
  }

  return map[field]
}

export const getReadableAnimalHairLength = (field: AnimalDtoHairLength): string => {
  const map: Record<AnimalDtoHairLength, string> = {
    [AnimalDtoHairLength.Long]: 'Long',
    [AnimalDtoHairLength.Medium]: 'Mi-long',
    [AnimalDtoHairLength.Short]: 'Court',
  }

  return map[field]
}

export const getReadableSterilizedForFerrets = (field: AnimalHealthDtoSterilizedForFerrets): string => {
  const map: Record<AnimalHealthDtoSterilizedForFerrets, string> = {
    [AnimalHealthDtoSterilizedForFerrets.Implanted]: 'Implanté(e)',
    [AnimalHealthDtoSterilizedForFerrets.NotSterilized]: 'Non stérilisé(e)',
    [AnimalHealthDtoSterilizedForFerrets.Sterilized]: 'Stérilisé(e)',
    [AnimalHealthDtoSterilizedForFerrets.Unknown]: 'Ne sais pas',
  }

  return map[field]
}

export const getReadableAnimalHealthPathology = (pathology: AnimalHealthDtoPathologies): string => {
  const map: Record<AnimalHealthDtoPathologies, string> = {
    [AnimalHealthDtoPathologies.CanineDistemper]: 'Maladie de carré',
    [AnimalHealthDtoPathologies.RubarthHepatitis]: 'Hépatite de Rubarth',
    [AnimalHealthDtoPathologies.Leptospirosis]: 'Leptospirose',
    [AnimalHealthDtoPathologies.Rabies]: 'Rage',
    [AnimalHealthDtoPathologies.Parvovirus]: 'Parvovirose',
    [AnimalHealthDtoPathologies.KennelCough]: 'Toux de chenil',
    [AnimalHealthDtoPathologies.Herpesvirus]: 'Herpèsvirose',
    [AnimalHealthDtoPathologies.Babesiosis]: 'Piroplasmose',
    [AnimalHealthDtoPathologies.LymeDisease]: 'Maladie de Lyme',
    [AnimalHealthDtoPathologies.Diabetic]: 'Diabétique',
    [AnimalHealthDtoPathologies.VisuallyImpaired]: 'Malvoyant',
    [AnimalHealthDtoPathologies.Deaf]: 'Sourd',
    [AnimalHealthDtoPathologies.Typhus]: 'Typhus',
    [AnimalHealthDtoPathologies.Chlamydiosis]: 'Chlamydiose',
    [AnimalHealthDtoPathologies.Coryza]: 'Coryza',
    [AnimalHealthDtoPathologies.FeLv]: 'FeLV',
    [AnimalHealthDtoPathologies.Fip]: 'PIF',
    [AnimalHealthDtoPathologies.Fiv]: 'FIV',
    [AnimalHealthDtoPathologies.CatScratchDisease]: 'Maladie des griffes du chat',
    [AnimalHealthDtoPathologies.Others]: 'Autres',
    [AnimalHealthDtoPathologies.Giardiasis]: 'Giardiose',
    [AnimalHealthDtoPathologies.RenalFailure]: 'Insuffisance rénale',
    [AnimalHealthDtoPathologies.HepaticInsufficiency]: 'Insuffisance hépatique',
    [AnimalHealthDtoPathologies.Epilepsy]: 'Épileptique',
    [AnimalHealthDtoPathologies.Cancer]: 'Cancer',
    [AnimalHealthDtoPathologies.Leishmaniasis]: 'Leishmaniose',
    [AnimalHealthDtoPathologies.ChronicGingivitis]: 'Gingivite chronique',
    [AnimalHealthDtoPathologies.InjuryTrauma]: 'Blessures / Traumatismes',
    [AnimalHealthDtoPathologies.Calicivirus]: 'Calicivirus',
    [AnimalHealthDtoPathologies.HeartInsufficiency]: 'Insuffisance cardiaque',
    [AnimalHealthDtoPathologies.Hyperthyroidism]: 'Hyperthyroïdie',
    [AnimalHealthDtoPathologies.Hypothyroidism]: 'Hypothyroïdie',
    [AnimalHealthDtoPathologies.Coronavirus]: 'Coronavirus',
  }

  return map[pathology] || 'Unknown Pathology'
}
